import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Modal, ModalBody, ModalFooter, Row, Spinner, Table } from "reactstrap";
import Select from "react-select";
import { useCompanies } from "../../../../hooks/companies";
import { useToast } from "../../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import MetricCard from "../../../../components/MetricCard";
import MetricCardGroup from "../../../../components/MetricCardGroup";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "../../../../components/PageHero";
import FilterBand from "../../../../components/FilterBand";
import SortHeader from "../../../../components/SortHeader";
import TablePagination from "../../../../components/TablePagination";
import { listMinhasTransportadoras, removerMinhaTransportadora, reenviarHomologacaoMinhaTransportadora } from "../../../../services/carteiraTransportadoraApi";
import { cancelarConvite, listarConvitesCuradoria, reenviarConvite } from "../../../../services/conviteTransportadoraApi";
import AdicionarTransportadoraModal from "./components/AdicionarTransportadoraModal";
import { conviteEstado } from "./conviteEstado";
import { esteiraEstado } from "./esteiraEstado";
import "../../../../components/ChecklistRow/checklistRow.css";
import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import ShellCard, { TableSurface } from "../../../../components/ShellCard";
import { reactSelectStyles, reactSelectPortalProps } from "../../../../utils/reactSelectStyles";
import { exportCsv } from "../../../../utils/exportCsv";
import { paginate, sortRows, toggleSort } from "../../../../utils/tableHelpers";
import DetalheTransportadoraModal from "../DetalheTransportadoraModal";
import TransportadoraMonogram from "../TransportadoraMonogram";
import { CapacidadeBadge, SftpBadge, StatusPill } from "../capacidadeBadges";

// Fonte segue literalmente o texto original de H1/H2: SSW / API própria /
// SFTP / Sem conector (decisão da spec). "SFTP" é ortogonal ao conector de
// cotação — um item pode ter conector "Sem conector" e ainda casar com o
// filtro "SFTP" se tiver CT-e/Fatura configurados por arquivo.
const FONTES = ["SSW", "API própria", "SFTP", "Sem conector"];
const matchFonte = (item, fonte) => {
  if (!fonte) return true;
  if (fonte === "SFTP") return !!(item.sftp && ((item.sftp.cte && item.sftp.cte.configurado) || (item.sftp.fatura && item.sftp.fatura.configurado)));
  return item.fonte === fonte;
};

// "Minhas Transportadoras" (carteira do embarcador) — PO1-2620, H1. Fetch
// único com pageSize generoso (a carteira de um embarcador é bem menor que o
// catálogo inteiro); busca, ordenação e paginação são client-side.
// PO1-2726: padrão visual do Hub (PageHero, FilterBand client, Table padrão
// com SortHeader/TablePagination, export CSV, zero cor cravada).
// PO1-3283 (épico PO1-3279): a entrada é uma só, "Adicionar transportadora"
// (CNPJ + contatos); Convidar e Conectar saíram da linha. A coluna Portal
// virou só informação e o convite existente ainda pode ser reenviado no
// drawer. Vínculo DEVOLVIDO ganha "Reenviar para homologação" na linha.

// Rótulo do card de curadoria por estado do item (PO1-2299 + pedidos PO1-3283).
const CURADORIA_ESTADO = {
  EM_CURADORIA: { rotulo: "Ajuste de matriz", badge: "pend" },
  RECUSADO_EVERLOG: { rotulo: "Recusado pela Everlog", badge: "lock" },
  PEDIDO_EMBARCADOR: { rotulo: "Pedido enviado", badge: "auto" },
  PEDIDO_INICIADO: { rotulo: "Onboarding iniciado", badge: "done" },
};
const PAGE_SIZE_FETCH = 2000;
const DEFAULT_SORT = { field: "fantasia", dir: "asc" };
const ESTEIRA_OPTIONS = [
  { value: "", label: "Todas" },
  { value: "aguardando-homologacao", label: "Aguardando homologação" },
  { value: "devolvido", label: "Devolvido" },
  { value: "em-onboarding", label: "Em onboarding" },
  { value: "operacional", label: "Operacional" },
];

const FONTE_OPTIONS = [{ value: "", label: "Todas as fontes" }].concat(FONTES.map((f) => ({ value: f, label: f })));
const COTACAO_OPTIONS = [
  { value: "", label: "Todas" },
  { value: "ativo", label: "Ativa" },
  { value: "pendente", label: "Pendente" },
  { value: "erro_credencial", label: "Erro de credencial" },
  { value: "sem", label: "Sem conector" },
];
const SFTP_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "recebendo", label: "Recebendo" },
  { value: "sem_recepcao", label: "Sem recepção" },
  { value: "nao_configurado", label: "Não configurado" },
];
const FILTROS_VAZIOS = { fonte: "", cotacao: "", sftp: "", esteira: "" };

const fieldLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.76rem",
  marginBottom: "0.35rem",
};

const stateBoxStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.86rem",
  padding: "2rem 1rem",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.6rem",
};

const formatDoc = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return v || "-";
};

const temCapacidade = (item, codigo, status) =>
  (item.capacidades || []).some((c) => c.codigo === codigo && (!status || c.status === status));

const temSftpSemRecepcao = (item) =>
  !!(item.sftp && ((item.sftp.cte && item.sftp.cte.semRecepcao) || (item.sftp.fatura && item.sftp.fatura.semRecepcao)));

const capCotacao = (item) => (item.capacidades || []).find((c) => c.codigo === "cap_cotacao");

// Status textual para filtro e export (mesma leitura dos badges da tabela).
const statusCotacao = (item) => {
  const cap = capCotacao(item);
  return cap && cap.status ? cap.status : "sem";
};
const LABEL_COTACAO = { ativo: "Ativa", pendente: "Pendente", erro_credencial: "Erro de credencial", sem: "Sem conector" };

const statusSftp = (item) => {
  const cte = item.sftp && item.sftp.cte;
  if (!cte || !cte.configurado) return "nao_configurado";
  return cte.semRecepcao ? "sem_recepcao" : "recebendo";
};
const LABEL_SFTP = { recebendo: "Recebendo", sem_recepcao: "Sem recepção", nao_configurado: "Não configurado" };

// Remover da carteira desliga a recepção por SFTP do par (backend,
// TransportadoraRepository.unlinkEmbarcador) e o modal avisa antes. null quando
// não há SFTP configurado; senão se recebeu nos últimos 7 dias e há quantos
// dias chegou o último arquivo (CT-e ou Fatura, o mais recente).
const sftpDaRemocao = (item) => {
  const tipos = [item?.sftp?.cte, item?.sftp?.fatura].filter((t) => t && t.configurado);
  if (!tipos.length) return null;
  const dias = tipos.map((t) => t.diasSemArquivo).filter((d) => d !== null && d !== undefined);
  return {
    recebendo: tipos.some((t) => t.semRecepcao === false),
    diasSemArquivo: dias.length ? Math.min(...dias) : null,
  };
};

const normaliza = (v) =>
  String(v || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const matchBusca = (item, termo) => {
  if (!termo) return true;
  const t = normaliza(termo);
  const digits = termo.replace(/\D/g, "");
  return (
    normaliza(item.fantasia).includes(t) ||
    normaliza(item.razaoSocial).includes(t) ||
    (digits.length > 0 && String(item.cnpjCpf || "").replace(/\D/g, "").includes(digits))
  );
};

const sortValue = (item, field) => {
  if (field === "fantasia") return item.fantasia || item.razaoSocial || "";
  if (field === "cnpjCpf") return String(item.cnpjCpf || "").replace(/\D/g, "");
  if (field === "fonte") return item.fonte || "";
  return item[field];
};

const contarFiltros = (f) => (f.fonte ? 1 : 0) + (f.cotacao ? 1 : 0) + (f.sftp ? 1 : 0) + (f.esteira ? 1 : 0);

export default function MinhasTransportadoras() {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();
  const embarcadorId = empresa?.value || null;

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [filtroRapido, setFiltroRapido] = useState(null); // 'cotacao' | 'erro' | 'semRecepcao' | null
  const [busca, setBusca] = useState("");
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [filtrosOpen, setFiltrosOpen] = useState(false);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [removerAlvo, setRemoverAlvo] = useState(null);
  const [removendo, setRemovendo] = useState(false);
  // PO1-2299: `conviteHabilitado` vem da carteira (undefined = escopo inválido /
  // API antiga: o card de curadoria e o reenvio de convite ficam escondidos).
  const [conviteHabilitado, setConviteHabilitado] = useState(undefined);
  const [adicionarAberto, setAdicionarAberto] = useState(false);
  const [reenviandoHomologacao, setReenviandoHomologacao] = useState(null); // id da transportadora
  const [curadoria, setCuradoria] = useState([]);
  const [cancelarConviteAlvo, setCancelarConviteAlvo] = useState(null);
  const [agindoConvite, setAgindoConvite] = useState(false);

  // PO1-3281: "corrigi, enviar de novo" por quem deu a entrada. Só o par deste
  // embarcador volta para a fila da Everlog.
  const reenviarHomologacao = useCallback(
    async (row) => {
      if (!embarcadorId || !row) return;
      try {
        setReenviandoHomologacao(row.id);
        await reenviarHomologacaoMinhaTransportadora({ embarcadorId, transportadoraId: row.id });
        addToast({
          place: "tr",
          type: "success",
          title: "Reenviada para homologação",
          description: `${row.fantasia || row.razaoSocial} voltou para a fila da Everlog.`,
        });
        carregar();
      } catch (e) {
        const d = (e && e.response && e.response.data) || {};
        addToast({
          place: "tr",
          type: "danger",
          title: "Não foi possível reenviar",
          description: d.pendencias ? d.pendencias.join("; ") : d.error || "Tente de novo.",
        });
      } finally {
        setReenviandoHomologacao(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [embarcadorId, addToast]
  );

  const carregarCuradoria = useCallback(async () => {
    if (!embarcadorId) return;
    try {
      const data = await listarConvitesCuradoria({ embarcadorId });
      setCuradoria((data && data.items) || []);
    } catch (_) {
      setCuradoria([]);
    }
  }, [embarcadorId]);

  const carregar = useCallback(async () => {
    if (!embarcadorId) return;
    setLoading(true);
    setErro("");
    try {
      const data = await listMinhasTransportadoras({ embarcadorId, page: 1, pageSize: PAGE_SIZE_FETCH });
      setItems((data && data.items) || []);
      setConviteHabilitado(data ? data.conviteHabilitado : undefined);
      if (data && data.conviteHabilitado !== undefined) carregarCuradoria();
    } catch (e) {
      const mensagem = e?.response?.data?.error || "Falha ao carregar suas transportadoras.";
      setErro(mensagem);
      addToast({ place: "tr", type: "danger", title: "Erro", description: mensagem });
    } finally {
      setLoading(false);
    }
  }, [embarcadorId, addToast, carregarCuradoria]);

  const reenviar = async (convite) => {
    if (!convite || !embarcadorId) return;
    setAgindoConvite(true);
    try {
      const { convite: novo } = await reenviarConvite({ conviteId: convite.id, embarcadorId });
      const falhou = novo && novo.estado === "FALHA_ENVIO";
      addToast({
        place: "tr",
        type: falhou ? "warning" : "success",
        title: falhou ? "E-mail não saiu" : "Convite reenviado",
        description: falhou ? "O envio falhou de novo. Confira o e-mail do contato." : `Novo link enviado para ${novo ? novo.email : ""}. O anterior deixou de valer.`,
      });
      carregar();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Erro", description: e?.response?.data?.error || "Falha ao reenviar o convite." });
    } finally {
      setAgindoConvite(false);
    }
  };

  const confirmarCancelarConvite = async () => {
    if (!cancelarConviteAlvo || !embarcadorId) return;
    setAgindoConvite(true);
    try {
      await cancelarConvite({ conviteId: cancelarConviteAlvo.id, embarcadorId });
      addToast({ place: "tr", type: "success", title: "Convite cancelado", description: "O link enviado deixou de valer." });
      setCancelarConviteAlvo(null);
      carregar();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Erro", description: e?.response?.data?.error || "Falha ao cancelar o convite." });
    } finally {
      setAgindoConvite(false);
    }
  };

  useEffect(() => {
    setSelectedId(null);
    carregar();
  }, [carregar]);

  // Qualquer mudança de recorte volta para a página 1.
  useEffect(() => {
    setPage(1);
  }, [busca, filtroRapido, filtros, sort, pageSize]);

  const confirmarRemocao = async () => {
    if (!removerAlvo || !embarcadorId) return;
    setRemovendo(true);
    try {
      await removerMinhaTransportadora({ embarcadorId, transportadoraId: removerAlvo.id });
      addToast({ place: "tr", type: "success", title: "Removida", description: "Transportadora removida da sua carteira." });
      setRemoverAlvo(null);
      setSelectedId(null);
      carregar();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Erro", description: e?.response?.data?.error || "Falha ao remover da carteira." });
    } finally {
      setRemovendo(false);
    }
  };

  const resumo = useMemo(
    () => ({
      total: items.length,
      cotacaoAtiva: items.filter((i) => temCapacidade(i, "cap_cotacao", "ativo")).length,
      erroCredencial: items.filter((i) => (i.capacidades || []).some((c) => c.status === "erro_credencial")).length,
      sftpSemRecepcao: items.filter(temSftpSemRecepcao).length,
    }),
    [items]
  );

  const itemsFiltrados = useMemo(() => {
    let base = items;
    if (filtroRapido === "cotacao") base = base.filter((i) => temCapacidade(i, "cap_cotacao", "ativo"));
    else if (filtroRapido === "erro") base = base.filter((i) => (i.capacidades || []).some((c) => c.status === "erro_credencial"));
    else if (filtroRapido === "semRecepcao") base = base.filter(temSftpSemRecepcao);
    if (filtros.fonte) base = base.filter((i) => matchFonte(i, filtros.fonte));
    if (filtros.cotacao) base = base.filter((i) => statusCotacao(i) === filtros.cotacao);
    if (filtros.sftp) base = base.filter((i) => statusSftp(i) === filtros.sftp);
    if (filtros.esteira) base = base.filter((i) => esteiraEstado(i.esteira).tipo === filtros.esteira);
    if (busca) base = base.filter((i) => matchBusca(i, busca));
    return sortRows(base, sort, sortValue);
  }, [items, filtroRapido, filtros, busca, sort]);

  const pagina = useMemo(() => paginate(itemsFiltrados, page, pageSize), [itemsFiltrados, page, pageSize]);

  const filtrosAtivosCount = contarFiltros(filtros);
  const temRecorte = Boolean(busca) || filtrosAtivosCount > 0 || Boolean(filtroRapido);

  const limparTudo = () => {
    setBusca("");
    setFiltros(FILTROS_VAZIOS);
    setFiltroRapido(null);
  };

  const exportar = () => {
    const ok = exportCsv({
      filename: "minhas_transportadoras",
      columns: [
        { label: "Apelido", getValue: (r) => r.fantasia || "" },
        { label: "Razão social", getValue: (r) => r.razaoSocial || "" },
        { label: "CNPJ/CPF", getValue: (r) => formatDoc(r.cnpjCpf) },
        { label: "Fonte", getValue: (r) => r.fonte || "" },
        { label: "Cotação", getValue: (r) => LABEL_COTACAO[statusCotacao(r)] || "" },
        { label: "SFTP", getValue: (r) => LABEL_SFTP[statusSftp(r)] || "" },
        { label: "Esteira", getValue: (r) => esteiraEstado(r.esteira).rotulo },
      ],
      rows: itemsFiltrados,
    });
    if (!ok) {
      addToast({ place: "tr", type: "warning", title: "Nada para exportar", description: "Nenhuma transportadora no recorte atual." });
    }
  };

  if (!hasEmpresaSelection) {
    return (
      <div className="content minhas-transportadoras-page">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para ver suas transportadoras." />
      </div>
    );
  }

  const sftpRemocao = sftpDaRemocao(removerAlvo);

  return (
    <div className="content minhas-transportadoras-page">
      <PageHero
        eyebrow="Conexões"
        title="Minhas Transportadoras"
        description="Transportadoras vinculadas à sua carteira, com capacidades e status de conexão."
        actions={
          <>
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={loading || !itemsFiltrados.length} />
            <Button color="primary" className="btn-sm" onClick={() => setAdicionarAberto(true)}>
              Adicionar transportadora
            </Button>
          </>
        }
      />

      {curadoria.length > 0 && (
        <ShellCard
          title="Transportadoras aguardando a Everlog"
          description="Pedidos de transportadora que a Everlog ainda vai iniciar, e entradas travadas porque a matriz da transportadora ainda não é a 0001 (a Everlog ajusta o cadastro). Recusas aparecem aqui por 30 dias."
          style={{ marginTop: "1rem" }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            {curadoria.map((c) => (
              <div
                key={c.id}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", padding: "0.5rem 0.7rem", borderRadius: 8, background: AUTOPILOT_COLORS.insetSurface, fontSize: "0.86rem" }}
              >
                <span>
                  <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{(c.transportadora && (c.transportadora.fantasia || c.transportadora.razaoSocial)) || c.nomeSugerido || "—"}</span>
                  <span style={{ color: AUTOPILOT_COLORS.muted }}> · {formatDoc(c.cnpj)}{c.email ? ` · ${c.email}` : ""}</span>
                  {c.estado === "RECUSADO_EVERLOG" && c.motivoRecusa && (
                    <span style={{ color: AUTOPILOT_COLORS.muted }}> · Motivo: {c.motivoRecusa}</span>
                  )}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span className={`onb-badge ${(CURADORIA_ESTADO[c.estado] || { badge: "lock" }).badge}`}>
                    {(CURADORIA_ESTADO[c.estado] || { rotulo: c.estado }).rotulo}
                  </span>
                  {c.estado === "EM_CURADORIA" && (
                    <Button size="sm" color="secondary" outline onClick={() => setCancelarConviteAlvo(c)}>
                      Cancelar
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </ShellCard>
      )}

      <FilterBand
        mode="client"
        searchLabel="Buscar transportadora"
        searchHint="Apelido, razão social ou CNPJ"
        searchPlaceholder="Digite o apelido, a razão social ou o CNPJ"
        searchValue={busca}
        onSearchChange={setBusca}
        activeFiltersCount={filtrosAtivosCount}
        open={filtrosOpen}
        onToggle={setFiltrosOpen}
        panelTitle="Filtros"
        panelDescription="Fonte do conector, status da cotação, recepção SFTP e fase da esteira."
        onClear={temRecorte ? limparTudo : undefined}
        showActions
      >
        <Row>
          <Col md={3}>
            <div style={fieldLabelStyle}>Fonte</div>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={FONTE_OPTIONS}
              value={FONTE_OPTIONS.find((o) => o.value === filtros.fonte) || FONTE_OPTIONS[0]}
              onChange={(opt) => setFiltros((prev) => ({ ...prev, fonte: (opt && opt.value) || "" }))}
              isSearchable={false}
            />
          </Col>
          <Col md={3}>
            <div style={fieldLabelStyle}>Cotação</div>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={COTACAO_OPTIONS}
              value={COTACAO_OPTIONS.find((o) => o.value === filtros.cotacao) || COTACAO_OPTIONS[0]}
              onChange={(opt) => setFiltros((prev) => ({ ...prev, cotacao: (opt && opt.value) || "" }))}
              isSearchable={false}
            />
          </Col>
          <Col md={3}>
            <div style={fieldLabelStyle}>SFTP</div>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={SFTP_OPTIONS}
              value={SFTP_OPTIONS.find((o) => o.value === filtros.sftp) || SFTP_OPTIONS[0]}
              onChange={(opt) => setFiltros((prev) => ({ ...prev, sftp: (opt && opt.value) || "" }))}
              isSearchable={false}
            />
          </Col>
          <Col md={3}>
            <div style={fieldLabelStyle}>Esteira</div>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={ESTEIRA_OPTIONS}
              value={ESTEIRA_OPTIONS.find((o) => o.value === filtros.esteira) || ESTEIRA_OPTIONS[0]}
              onChange={(opt) => setFiltros((prev) => ({ ...prev, esteira: (opt && opt.value) || "" }))}
              isSearchable={false}
            />
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup
        title="Situação das transportadoras"
        description="Clique em um indicador para filtrar a lista; clique de novo para desfazer."
      >
        <MetricCard
          label="Na carteira"
          value={resumo.total}
          tone="neutral"
          iconClassName="tim-icons icon-bus-front-12"
          active={!filtroRapido}
          onClick={() => setFiltroRapido(null)}
        />
        <MetricCard
          label="Cotação ativa"
          value={resumo.cotacaoAtiva}
          tone="success"
          iconClassName="tim-icons icon-check-2"
          active={filtroRapido === "cotacao"}
          onClick={() => setFiltroRapido(filtroRapido === "cotacao" ? null : "cotacao")}
        />
        <MetricCard
          label="Erro de credencial"
          value={resumo.erroCredencial}
          tone="warning"
          iconClassName="tim-icons icon-alert-circle-exc"
          active={filtroRapido === "erro"}
          onClick={() => setFiltroRapido(filtroRapido === "erro" ? null : "erro")}
        />
        <MetricCard
          label="SFTP sem recepção"
          value={resumo.sftpSemRecepcao}
          tone="pink"
          iconClassName="tim-icons icon-cloud-download-93"
          active={filtroRapido === "semRecepcao"}
          onClick={() => setFiltroRapido(filtroRapido === "semRecepcao" ? null : "semRecepcao")}
        />
      </MetricCardGroup>

      <ShellCard
        title="Transportadoras"
        description={
          loading
            ? "Carregando..."
            : `${itemsFiltrados.length} de ${items.length} transportadora(s)${temRecorte ? " no recorte" : " na carteira"}`
        }
        style={{ marginTop: "1.25rem" }}
      >

            {loading ? (
              <div style={stateBoxStyle}>
                <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
                <span>Carregando suas transportadoras...</span>
              </div>
            ) : erro ? (
              <div style={stateBoxStyle}>
                <span style={{ color: AUTOPILOT_COLORS.statusDanger }}>{erro}</span>
                <Button size="sm" color="info" outline onClick={carregar}>
                  Tentar novamente
                </Button>
              </div>
            ) : !itemsFiltrados.length ? (
              <div style={stateBoxStyle}>
                <span>
                  {temRecorte
                    ? "Nenhuma transportadora no recorte atual."
                    : "Sua carteira ainda não tem transportadoras. Use Adicionar transportadora ou o Catálogo."}
                </span>
                {temRecorte && (
                  <Button size="sm" color="info" outline onClick={limparTudo}>
                    Limpar busca e filtros
                  </Button>
                )}
              </div>
            ) : (
              <TableSurface>
                <div className="table-responsive">
                  <Table size="sm" hover className="minhas-transportadoras-table hub-thead-accent">
                    <thead className="text-primary">
                      <tr>
                        <th>
                          <SortHeader label="Apelido" field="fantasia" sort={sort} onSort={(f) => setSort(toggleSort(sort, f))} />
                        </th>
                        <th>
                          <SortHeader label="CNPJ/CPF" field="cnpjCpf" sort={sort} onSort={(f) => setSort(toggleSort(sort, f))} />
                        </th>
                        <th>
                          <SortHeader label="Fonte" field="fonte" sort={sort} onSort={(f) => setSort(toggleSort(sort, f))} />
                        </th>
                        <th>Cotação</th>
                        <th>SFTP</th>
                        <th>Esteira</th>
                        <th>Portal</th>
                        {/* PO1-3167: coluna de ação sem cabeçalho — a linha inteira já abre o detalhe, o botão só anuncia. */}
                        <th aria-label="Ações" />
                      </tr>
                    </thead>
                    <tbody>
                      {pagina.items.map((row) => {
                        const cap = capCotacao(row);
                        const ativo = row.id === selectedId;
                        const acesso = conviteEstado(row.acessoPortal, { conviteHabilitado: !!conviteHabilitado, esteira: row.esteira });
                        const esteira = esteiraEstado(row.esteira);
                        return (
                          <tr
                            key={row.id}
                            onClick={() => setSelectedId(row.id)}
                            style={{
                              cursor: "pointer",
                              background: ativo ? AUTOPILOT_COLORS.insetSurfaceActive : undefined,
                              // PO1-3167: linha aberta no drawer ganha a borda interna de destaque
                              // (mesmo padrão de Admin/Transportadoras), não só o fundo.
                              boxShadow: ativo ? `inset 0 0 0 1px ${AUTOPILOT_COLORS.accent}` : undefined,
                            }}
                          >
                            <td style={{ verticalAlign: "middle" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
                                <TransportadoraMonogram nome={row.fantasia || row.razaoSocial} size={28} logoUrl={row.logoUrl} />
                                <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{row.fantasia || row.razaoSocial}</span>
                              </div>
                            </td>
                            <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>{formatDoc(row.cnpjCpf)}</td>
                            <td style={{ verticalAlign: "middle" }}>{row.fonte || "—"}</td>
                            <td style={{ verticalAlign: "middle" }}>
                              {cap ? <CapacidadeBadge {...cap} /> : <StatusPill status="sem-conector" labelOverride="—" />}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              {row.sftp ? (
                                <SftpBadge
                                  configurado={!!(row.sftp.cte && row.sftp.cte.configurado)}
                                  semRecepcao={row.sftp.cte && row.sftp.cte.semRecepcao}
                                  embarcadorTemFlow={row.sftp.embarcadorTemFlow}
                                />
                              ) : null}
                            </td>
                            <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                              <span className={`onb-badge ${esteira.badge}`} title={esteira.ajuda}>
                                {esteira.rotulo}
                              </span>
                              {esteira.podeReenviar ? (
                                <Button
                                  size="sm"
                                  color="primary"
                                  outline
                                  className="ml-2"
                                  disabled={reenviandoHomologacao === row.id}
                                  title={esteira.ajuda}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    reenviarHomologacao(row);
                                  }}
                                >
                                  {reenviandoHomologacao === row.id ? "Reenviando…" : "Reenviar para homologação"}
                                </Button>
                              ) : null}
                            </td>
                            {/* PO1-3283: a coluna Portal virou só informação. Convidar e Conectar saíram:
                                a entrada é "Adicionar transportadora", e o convite nasce no onboarding. */}
                            <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                              {acesso.tipo === "sem-escopo" ? (
                                <span style={{ color: AUTOPILOT_COLORS.muted }}>—</span>
                              ) : (
                                <span className={`onb-badge ${acesso.badge}`} title={acesso.convite && acesso.convite.email ? acesso.convite.email : ""}>
                                  {acesso.rotulo}
                                </span>
                              )}
                            </td>
                            {/* PO1-3167: botão-ícone (a tabela já tem 7 colunas; texto aqui não cabe). Mesmo
                                stopPropagation da coluna Portal: o clique não borbulha para a <tr>.
                                Cor de destaque porque, no cinza padrão do btn-icon, o ícone sumia ao
                                lado dos botões da linha — e afordância é o ponto da história. */}
                            <td className="text-right" style={{ verticalAlign: "middle", whiteSpace: "nowrap" }} onClick={(event) => event.stopPropagation()}>
                              <Button
                                type="button"
                                color="link"
                                size="sm"
                                className="btn-icon"
                                title="Ver detalhes"
                                aria-label={`Ver detalhes de ${row.fantasia || row.razaoSocial}`}
                                aria-pressed={ativo}
                                onClick={() => setSelectedId(ativo ? null : row.id)}
                                style={{ color: AUTOPILOT_COLORS.accent }}
                              >
                                <i className="tim-icons icon-zoom-split" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <TablePagination
                  page={pagina.page}
                  pageSize={pageSize}
                  total={itemsFiltrados.length}
                  itemLabel="transportadora(s)"
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </TableSurface>
            )}
      </ShellCard>

      {/* PO1-3856: detalhe em modal (antes era painel lateral no grid 1fr 420px). */}
      <DetalheTransportadoraModal
        transportadoraId={selectedId}
        embarcadorId={embarcadorId}
        onClose={() => setSelectedId(null)}
        onChanged={carregar}
        onRemoverDaCarteira={() => setRemoverAlvo(items.find((i) => i.id === selectedId) || { id: selectedId })}
        acessoPortal={(items.find((i) => i.id === selectedId) || {}).acessoPortal}
        conviteHabilitado={!!conviteHabilitado}
        onReenviar={reenviar}
        onCancelar={(convite) => setCancelarConviteAlvo(convite)}
      />

      <AdicionarTransportadoraModal
        isOpen={adicionarAberto}
        embarcadorId={embarcadorId}
        onClose={() => setAdicionarAberto(false)}
        onAdicionada={() => carregar()}
      />

      <Modal isOpen={!!cancelarConviteAlvo} toggle={() => setCancelarConviteAlvo(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", marginBottom: "0.5rem", fontWeight: 600 }}>Cancelar convite</div>
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.86rem" }}>
            O link enviado para <strong style={{ color: AUTOPILOT_COLORS.title }}>{cancelarConviteAlvo?.email}</strong> deixa de valer. Você pode convidar de novo depois.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setCancelarConviteAlvo(null)} disabled={agindoConvite}>
            Voltar
          </Button>
          <Button color="danger" onClick={confirmarCancelarConvite} disabled={agindoConvite}>
            {agindoConvite ? <Spinner size="sm" /> : "Cancelar convite"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!removerAlvo} toggle={() => setRemoverAlvo(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", marginBottom: "0.5rem", fontWeight: 600 }}>
            Remover da carteira
          </div>
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.86rem" }}>
            Deseja realmente remover{" "}
            <strong style={{ color: AUTOPILOT_COLORS.title }}>{removerAlvo?.fantasia || removerAlvo?.razaoSocial}</strong>{" "}
            da sua carteira?
          </div>
          {sftpRemocao && (
            <div
              style={{
                marginTop: "0.75rem",
                padding: "0.65rem 0.8rem",
                borderRadius: 8,
                fontSize: "0.82rem",
                lineHeight: 1.5,
                color: sftpRemocao.recebendo ? AUTOPILOT_COLORS.statusWarning : AUTOPILOT_COLORS.text,
                background: sftpRemocao.recebendo ? AUTOPILOT_COLORS.statusWarningBg : AUTOPILOT_COLORS.insetSurface,
                border: `1px solid ${sftpRemocao.recebendo ? AUTOPILOT_COLORS.statusWarningBorder : AUTOPILOT_COLORS.border}`,
              }}
            >
              A recepção de CT-e e Fatura por SFTP desta transportadora também será desligada: arquivos novos deixam de
              ser processados. As pastas e os arquivos já recebidos continuam no SFTP.
              {sftpRemocao.recebendo && sftpRemocao.diasSemArquivo !== null && (
                <div style={{ fontWeight: 600, marginTop: "0.35rem" }}>
                  O último arquivo dela chegou há {sftpRemocao.diasSemArquivo} dia
                  {sftpRemocao.diasSemArquivo === 1 ? "" : "s"}.
                </div>
              )}
            </div>
          )}
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.86rem", marginTop: "0.75rem" }}>
            O cadastro da transportadora não é apagado: você pode readicioná-la depois pelo Catálogo
            {sftpRemocao ? ", e a recepção por SFTP volta como estava." : "."}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setRemoverAlvo(null)} disabled={removendo}>
            Cancelar
          </Button>
          <Button color="danger" onClick={confirmarRemocao} disabled={removendo}>
            {removendo ? <Spinner size="sm" /> : "Sim, remover"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
